import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
  } from "react";
  
  import {
    FiX,
    FiSend,
    FiCpu,
    FiChevronDown,
  } from "react-icons/fi";
  
  import {
    motion,
    AnimatePresence,
  } from "framer-motion";
  
  import axios from "axios";
  import assistent from "../../assets/assistent.png";
  
  // ======================================================
  // ANIMACIONES
  // ======================================================
  
  const chatVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.92 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 28,
      },
    },
    exit: {
      opacity: 0,
      y: 30,
      scale: 0.94,
      transition: {
        duration: 0.2,
        ease: "easeIn",
      },
    },
  };
  
  const messageVariants = {
    hidden: { opacity: 0, y: 12, scale: 0.97 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 30,
      },
    },
  };
  
  const tooltipVariants = {
    hidden: { opacity: 0, y: 8, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.18 },
    },
    exit: {
      opacity: 0,
      y: 6,
      scale: 0.95,
      transition: { duration: 0.14 },
    },
  };
  
  // ======================================================
  // TYPING DOTS
  // ======================================================
  
  const TypingDots = () => (
    <div className="flex items-center gap-[5px] py-1 px-1">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="w-2 h-2 rounded-full"
          style={{ background: "#C9A84C" }}
          animate={{
            y: [0, -5, 0],
            opacity: [0.4, 1, 0.4],
          }}
          transition={{
            duration: 0.9,
            repeat: Infinity,
            delay: i * 0.18,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
  
  // ======================================================
  // CHATBOT
  // ======================================================
  
  const ChatBot = () => {
    const [chatAbierto, setChatAbierto] = useState(false);
  
    const [mensajes, setMensajes] = useState([
      {
        texto:
          "Hola 😊 soy el asistente de IDM. Estoy acá para ayudarte con cualquier consulta.",
        origen: "asistente",
      },
    ]);
  
    const [nuevoMensaje, setNuevoMensaje] = useState("");
    const [enviandoMensaje, setEnviandoMensaje] = useState(false);
    const [mostrarTooltip, setMostrarTooltip] = useState(false);
    const [scrollVisible, setScrollVisible] = useState(false);
  
    // ======================================================
    // DRAG POSITION
    // ======================================================
  
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
  
    const dragStart = useRef(null);
    const didDrag = useRef(false);
  
    const mensajesRef = useRef(null);
    const inputRef = useRef(null);
  
    // ======================================================
    // RESPONSIVE CHAT SIZE
    // ======================================================
  
    const [viewport, setViewport] = useState({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  
    useEffect(() => {
      const resize = () => {
        setViewport({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      };
  
      window.addEventListener("resize", resize);
  
      return () => window.removeEventListener("resize", resize);
    }, []);
  
    // ======================================================
    // AUTO SCROLL
    // ======================================================
  
    useEffect(() => {
      const el = mensajesRef.current;
      if (!el) return;
  
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "smooth",
      });
    }, [mensajes]);
  
    // ======================================================
    // SCROLL INDICATOR
    // ======================================================
  
    useEffect(() => {
      const el = mensajesRef.current;
      if (!el) return;
  
      const handleScroll = () => {
        const dist =
          el.scrollHeight - el.scrollTop - el.clientHeight;
  
        setScrollVisible(dist > 80);
      };
  
      el.addEventListener("scroll", handleScroll);
  
      return () =>
        el.removeEventListener("scroll", handleScroll);
    }, [chatAbierto]);
  
    // ======================================================
    // FOCUS INPUT
    // ======================================================
  
    useEffect(() => {
      if (chatAbierto) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 300);
      }
    }, [chatAbierto]);
  
    // ======================================================
    // DRAG HANDLERS
    // ======================================================
  
    const handleMouseDown = useCallback(
      (e) => {
        if (e.button !== 0) return;
  
        dragStart.current = {
          mouseX: e.clientX,
          mouseY: e.clientY,
          posX: pos.x,
          posY: pos.y,
        };
  
        didDrag.current = false;
  
        const handleMouseMove = (ev) => {
          const dx =
            ev.clientX - dragStart.current.mouseX;
  
          const dy =
            ev.clientY - dragStart.current.mouseY;
  
          if (
            !isDragging &&
            (Math.abs(dx) > 4 || Math.abs(dy) > 4)
          ) {
            setIsDragging(true);
          }
  
          didDrag.current = true;
  
          setPos({
            x: dragStart.current.posX + dx,
            y: dragStart.current.posY + dy,
          });
        };
  
        const handleMouseUp = () => {
          setIsDragging(false);
  
          window.removeEventListener(
            "mousemove",
            handleMouseMove
          );
  
          window.removeEventListener(
            "mouseup",
            handleMouseUp
          );
        };
  
        window.addEventListener(
          "mousemove",
          handleMouseMove
        );
  
        window.addEventListener(
          "mouseup",
          handleMouseUp
        );
      },
      [pos, isDragging]
    );
  
    // ======================================================
    // TOUCH
    // ======================================================
  
    const handleTouchStart = useCallback(
      (e) => {
        const t = e.touches[0];
  
        dragStart.current = {
          mouseX: t.clientX,
          mouseY: t.clientY,
          posX: pos.x,
          posY: pos.y,
        };
  
        didDrag.current = false;
  
        const handleTouchMove = (ev) => {
          const touch = ev.touches[0];
  
          const dx =
            touch.clientX - dragStart.current.mouseX;
  
          const dy =
            touch.clientY - dragStart.current.mouseY;
  
          didDrag.current = true;
  
          setIsDragging(true);
  
          setPos({
            x: dragStart.current.posX + dx,
            y: dragStart.current.posY + dy,
          });
        };
  
        const handleTouchEnd = () => {
          setIsDragging(false);
  
          window.removeEventListener(
            "touchmove",
            handleTouchMove
          );
  
          window.removeEventListener(
            "touchend",
            handleTouchEnd
          );
        };
  
        window.addEventListener(
          "touchmove",
          handleTouchMove,
          { passive: false }
        );
  
        window.addEventListener(
          "touchend",
          handleTouchEnd
        );
      },
      [pos]
    );
  
    // ======================================================
    // CLICK ONLY IF NO DRAG
    // ======================================================
  
    const handleButtonClick = () => {
      if (didDrag.current) return;
  
      setChatAbierto((v) => !v);
    };
  
    // ======================================================
    // SEND MESSAGE
    // ======================================================
  
    const handleEnviarMensaje = async () => {
      if (!nuevoMensaje.trim() || enviandoMensaje) return;
  
      const texto = nuevoMensaje.trim();
  
      setNuevoMensaje("");
      setEnviandoMensaje(true);
  
      setMensajes((prev) => [
        ...prev,
        { texto, origen: "usuario" },
        {
          texto: "",
          origen: "asistente",
          temporal: true,
        },
      ]);
  
      try {
        const respuesta =
          await obtenerRespuestaDesdeServidor(texto);
  
        setMensajes((prev) =>
          prev.map((m) =>
            m.temporal
              ? {
                  texto: respuesta,
                  origen: "asistente",
                }
              : m
          )
        );
      } catch (error) {
        const textoError =
          error?.message ||
          "Hubo un problema al conectar con el asistente.";
  
        setMensajes((prev) =>
          prev.map((m) =>
            m.temporal
              ? {
                  texto: `Error: ${textoError}`,
                  origen: "asistente",
                  isError: true,
                }
              : m
          )
        );
      } finally {
        setEnviandoMensaje(false);
        inputRef.current?.focus();
      }
    };
  
    // ======================================================
    // API
    // ======================================================
  
    const obtenerRespuestaDesdeServidor = async (
      mensajeUsuario
    ) => {
      const endpoint =
        "https://medicinacondante.com/bot.php?recurso=apiai";
  
      try {
        const resp = await axios.post(
          endpoint,
          { mensaje: mensajeUsuario },
          {
            timeout: 20000,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
  
        if (resp.data?.error)
          throw new Error(resp.data.error);
  
        if (
          typeof resp.data?.respuesta === "string"
        ) {
          return resp.data.respuesta;
        }
  
        throw new Error(
          "Respuesta inválida del servidor"
        );
      } catch (err) {
        if (err.response) {
          throw new Error(
            err.response.data?.error ||
              `Error ${err.response.status}`
          );
        }
  
        if (err.request) {
          throw new Error(
            "El servidor no respondió"
          );
        }
  
        throw new Error(err.message);
      }
    };
  
    // ======================================================
    // ENTER
    // ======================================================
  
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleEnviarMensaje();
      }
    };
  
    // ======================================================
    // SCROLL TO BOTTOM
    // ======================================================
  
    const scrollAlFondo = () => {
      mensajesRef.current?.scrollTo({
        top: mensajesRef.current.scrollHeight,
        behavior: "smooth",
      });
    };
  
    // ======================================================
    // RESPONSIVE LIMITS
    // ======================================================
  
    const isMobile = viewport.width < 768;
  
    const CHAT_WIDTH = isMobile
      ? Math.min(viewport.width - 24, 420)
      : 340;
  
    const CHAT_HEIGHT = Math.min(
      540,
      viewport.height - 24
    );
  
    // ======================================================
    // CALCULATE SAFE POSITION
    // ======================================================
  
    const margin = 12;
  
    // posición real desde bottom-right
    const currentRight = 20 - pos.x;
    const currentBottom = 20 - pos.y;
  
    // convertir a top para validar overflow
    const calculatedTop =
      viewport.height -
      currentBottom -
      CHAT_HEIGHT;
  
    // si se sale arriba, ajustamos
    let safeTop = calculatedTop;
  
    if (safeTop < margin) {
      safeTop = margin;
    }
  
    // si se sale abajo
    if (
      safeTop + CHAT_HEIGHT >
      viewport.height - margin
    ) {
      safeTop =
        viewport.height -
        CHAT_HEIGHT -
        margin;
    }
  
    // evitar negativos
    safeTop = Math.max(margin, safeTop);
  
    // ======================================================
    // CONTAINER STYLE
    // ======================================================
  
    const containerStyle = {
      position: "fixed",
  
      // cuando el chat está abierto usamos top seguro
      ...(chatAbierto
        ? {
            top: safeTop,
            right: Math.max(margin, currentRight),
          }
        : {
            bottom: currentBottom,
            right: currentRight,
          }),
  
      zIndex: 999999,
      fontFamily: "Montserrat, sans-serif",
      userSelect: "none",
      cursor: isDragging ? "grabbing" : "auto",
    };
  
    return (
      <div style={containerStyle}>
        {/* ====================================================== */}
        {/* BOTÓN FLOTANTE */}
        {/* ====================================================== */}
  
        <AnimatePresence>
          {!chatAbierto && (
            <motion.button
              initial={{
                opacity: 0,
                scale: 0.7,
                rotate: -10,
              }}
              animate={{
                opacity: 1,
                scale: 1,
                rotate: 0,
              }}
              exit={{
                opacity: 0,
                scale: 0.7,
                rotate: 10,
              }}
              whileHover={
                !isDragging ? { scale: 1.08 } : {}
              }
              whileTap={
                !isDragging ? { scale: 0.93 } : {}
              }
              transition={{
                type: "spring",
                stiffness: 320,
                damping: 22,
              }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onClick={handleButtonClick}
              onMouseEnter={() =>
                setMostrarTooltip(true)
              }
              onMouseLeave={() =>
                setMostrarTooltip(false)
              }
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "#0D0D0D",
                border:
                  "1px solid rgba(201,168,76,0.35)",
                boxShadow: isDragging
                  ? "0 16px 48px rgba(0,0,0,0.7), 0 0 0 2px rgba(201,168,76,0.4)"
                  : "0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(201,168,76,0.1)",
                overflow: "hidden",
                cursor: isDragging
                  ? "grabbing"
                  : "grab",
                transition: "box-shadow 0.2s",
              }}
            >
              {!isDragging && (
                <motion.span
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    border:
                      "2px solid rgba(201,168,76,0.3)",
                  }}
                  animate={{
                    scale: [1, 1.35],
                    opacity: [0.6, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeOut",
                  }}
                />
              )}
  
              <img
                src={assistent}
                alt="Asistente"
                draggable={false}
                style={{
                  position: "relative",
                  zIndex: 10,
                  width: 44,
                  height: 44,
                  objectFit: "contain",
                }}
              />
            </motion.button>
          )}
        </AnimatePresence>
  
        {/* ====================================================== */}
        {/* TOOLTIP */}
        {/* ====================================================== */}
  
        <AnimatePresence>
          {mostrarTooltip &&
            !chatAbierto &&
            !isDragging && (
              <motion.div
                variants={tooltipVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{
                  position: "absolute",
                  bottom: 80,
                  right: 0,
                  background: "#111111",
                  border:
                    "1px solid rgba(201,168,76,0.2)",
                  padding: "12px 16px",
                  borderRadius: 16,
                  boxShadow:
                    "0 8px 32px rgba(0,0,0,0.5)",
                  width: 240,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      marginTop: 3,
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: "#4ade80",
                      flexShrink: 0,
                      boxShadow:
                        "0 0 10px rgba(74,222,128,0.8)",
                    }}
                  />
  
                  <div>
                    <p
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color:
                          "rgba(240,237,230,0.9)",
                        margin: 0,
                      }}
                    >
                      Asistente de IDM
                    </p>
  
                    <p
                      style={{
                        fontSize: 11,
                        marginTop: 4,
                        lineHeight: 1.6,
                        color:
                          "rgba(240,237,230,0.45)",
                        margin: "4px 0 0",
                      }}
                    >
                      Disponible ahora para ayudarte
                      con tus consultas.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
        </AnimatePresence>
  
        {/* ====================================================== */}
        {/* CHAT */}
        {/* ====================================================== */}
  
        <AnimatePresence>
          {chatAbierto && (
            <motion.div
              variants={chatVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{
                position: "relative",
  
                width: CHAT_WIDTH,
  
                height: CHAT_HEIGHT,
  
                maxWidth: "95vw",
  
                maxHeight: "95vh",
  
                background: "#0D0D0D",
  
                border:
                  "1px solid rgba(201,168,76,0.18)",
  
                borderRadius: isMobile ? 22 : 28,
  
                boxShadow:
                  "0 25px 80px rgba(0,0,0,0.7)",
  
                overflow: "hidden",
  
                display: "flex",
  
                flexDirection: "column",
              }}
            >
              {/* HEADER */}
              <div
                style={{
                  position: "relative",
                  padding: "16px 20px",
                  borderBottom:
                    "1px solid rgba(201,168,76,0.13)",
                  background:
                    "rgba(201,168,76,0.04)",
                }}
              >
                <motion.div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: 1,
                    width: "100%",
                    background:
                      "linear-gradient(90deg, #C9A84C 0%, transparent 100%)",
                    opacity: 0.5,
                  }}
                  initial={{
                    scaleX: 0,
                    originX: 0,
                  }}
                  animate={{ scaleX: 1 }}
                  transition={{
                    duration: 1,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                />
  
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        position: "relative",
                        cursor: "grab",
                      }}
                      onMouseDown={handleMouseDown}
                      onTouchStart={handleTouchStart}
                    >
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          background:
                            "rgba(201,168,76,0.08)",
                          border:
                            "1px solid rgba(201,168,76,0.25)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent:
                            "center",
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={assistent}
                          alt="Asistente"
                          draggable={false}
                          style={{
                            width: 32,
                            height: 32,
                            objectFit: "contain",
                          }}
                        />
                      </div>
  
                      <motion.div
                        style={{
                          position: "absolute",
                          bottom: 0,
                          right: 0,
                          width: 11,
                          height: 11,
                          borderRadius: "50%",
                          background: "#4ade80",
                          border:
                            "2px solid #0D0D0D",
                        }}
                        animate={{
                          boxShadow: [
                            "0 0 0px rgba(74,222,128,0)",
                            "0 0 10px rgba(74,222,128,1)",
                            "0 0 0px rgba(74,222,128,0)",
                          ],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                        }}
                      />
                    </div>
  
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <h2
                          style={{
                            color:
                              "rgba(240,237,230,0.9)",
                            fontWeight: 600,
                            fontSize: 14,
                            margin: 0,
                          }}
                        >
                          Asistente
                        </h2>
  
                        <FiCpu
                          style={{
                            color: "#C9A84C",
                          }}
                          size={13}
                        />
                      </div>
  
                      <p
                        style={{
                          fontSize: 11,
                          color:
                            "rgba(240,237,230,0.35)",
                          margin: 0,
                          letterSpacing: "0.1em",
                          textTransform:
                            "uppercase",
                        }}
                      >
                        IDM medicina
                      </p>
                    </div>
                  </div>
  
                  <motion.button
                    whileHover={{
                      rotate: 90,
                      scale: 1.1,
                    }}
                    whileTap={{ scale: 0.9 }}
                    transition={{
                      type: "spring",
                      stiffness: 400,
                      damping: 20,
                    }}
                    onClick={() =>
                      setChatAbierto(false)
                    }
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background:
                        "rgba(201,168,76,0.08)",
                      border:
                        "1px solid rgba(201,168,76,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        "center",
                      color:
                        "rgba(240,237,230,0.5)",
                      cursor: "pointer",
                    }}
                  >
                    <FiX size={15} />
                  </motion.button>
                </div>
              </div>
  
              {/* MENSAJES */}
              <div
                style={{
                  position: "relative",
                  flex: 1,
                  overflow: "hidden",
                }}
              >
                <div
                  ref={mensajesRef}
                  style={{
                    height: "100%",
                    overflowY: "auto",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {mensajes.map((mensaje, index) => (
                    <motion.div
                      key={index}
                      variants={messageVariants}
                      initial="hidden"
                      animate="visible"
                      style={{
                        padding: "10px 16px",
                        borderRadius: 20,
                        maxWidth: "82%",
                        fontSize: 14,
                        lineHeight: 1.65,
                        wordBreak: "break-word",
  
                        alignSelf:
                          mensaje.origen ===
                          "usuario"
                            ? "flex-end"
                            : "flex-start",
  
                        ...(mensaje.origen ===
                        "usuario"
                          ? {
                              background:
                                "#C9A84C",
                              color: "#000",
                              borderBottomRightRadius: 4,
                              fontWeight: 500,
                            }
                          : mensaje.isError
                          ? {
                              background:
                                "rgba(220,38,38,0.1)",
                              color: "#f87171",
                              border:
                                "1px solid rgba(220,38,38,0.25)",
                              borderBottomLeftRadius: 4,
                            }
                          : {
                              background:
                                "rgba(201,168,76,0.06)",
                              color:
                                "rgba(240,237,230,0.85)",
                              border:
                                "1px solid rgba(201,168,76,0.13)",
                              borderBottomLeftRadius: 4,
                            }),
                      }}
                    >
                      {mensaje.temporal ? (
                        <TypingDots />
                      ) : (
                        mensaje.texto
                      )}
                    </motion.div>
                  ))}
                </div>
  
                <AnimatePresence>
                  {scrollVisible && (
                    <motion.button
                      initial={{
                        opacity: 0,
                        y: 8,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      exit={{
                        opacity: 0,
                        y: 8,
                      }}
                      onClick={scrollAlFondo}
                      style={{
                        position: "absolute",
                        bottom: 12,
                        right: 12,
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        background: "#0D0D0D",
                        border:
                          "1px solid rgba(201,168,76,0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                          "center",
                        color: "#C9A84C",
                        cursor: "pointer",
                        boxShadow:
                          "0 4px 12px rgba(0,0,0,0.4)",
                      }}
                    >
                      <FiChevronDown size={15} />
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
  
              {/* INPUT */}
              <div
                style={{
                  padding: 16,
                  borderTop:
                    "1px solid rgba(201,168,76,0.13)",
                  background:
                    "rgba(201,168,76,0.04)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <input
                    ref={inputRef}
                    type="text"
                    value={nuevoMensaje}
                    onChange={(e) =>
                      setNuevoMensaje(
                        e.target.value
                      )
                    }
                    onKeyDown={handleKeyDown}
                    placeholder="Escribí tu consulta..."
                    disabled={enviandoMensaje}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      height: 46,
                      borderRadius: 23,
                      border:
                        "1px solid rgba(201,168,76,0.2)",
                      background:
                        "rgba(201,168,76,0.06)",
                      padding: "0 20px",
                      color:
                        "rgba(240,237,230,0.9)",
                      fontSize: 14,
                      outline: "none",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor =
                        "rgba(201,168,76,0.5)";
  
                      e.target.style.boxShadow =
                        "0 0 0 2px rgba(201,168,76,0.12)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor =
                        "rgba(201,168,76,0.2)";
  
                      e.target.style.boxShadow =
                        "none";
                    }}
                  />
  
                  <motion.button
                    onClick={
                      handleEnviarMensaje
                    }
                    disabled={
                      enviandoMensaje ||
                      !nuevoMensaje.trim()
                    }
                    whileHover={{
                      scale: 1.06,
                    }}
                    whileTap={{
                      scale: 0.92,
                    }}
                    style={{
                      flexShrink: 0,
                      width: 46,
                      height: 46,
                      borderRadius: "50%",
                      background:
                        enviandoMensaje ||
                        !nuevoMensaje.trim()
                          ? "rgba(201,168,76,0.2)"
                          : "#C9A84C",
  
                      color:
                        enviandoMensaje ||
                        !nuevoMensaje.trim()
                          ? "rgba(201,168,76,0.4)"
                          : "#000",
  
                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        "center",
  
                      cursor:
                        enviandoMensaje ||
                        !nuevoMensaje.trim()
                          ? "not-allowed"
                          : "pointer",
  
                      transition: "all 0.2s",
  
                      boxShadow:
                        !enviandoMensaje &&
                        nuevoMensaje.trim()
                          ? "0 4px 16px rgba(201,168,76,0.35)"
                          : "none",
  
                      border: "none",
                    }}
                  >
                    <AnimatePresence mode="wait">
                      {enviandoMensaje ? (
                        <motion.span
                          key="loading"
                          initial={{
                            opacity: 0,
                            rotate: -90,
                          }}
                          animate={{
                            opacity: 1,
                            rotate: 0,
                          }}
                          exit={{
                            opacity: 0,
                          }}
                          style={{
                            width: 16,
                            height: 16,
                            border:
                              "2px solid rgba(201,168,76,0.3)",
                            borderTopColor:
                              "#C9A84C",
                            borderRadius:
                              "50%",
                            display: "block",
                            animation:
                              "spin 0.7s linear infinite",
                          }}
                        />
                      ) : (
                        <motion.span
                          key="send"
                          initial={{
                            opacity: 0,
                            x: -4,
                          }}
                          animate={{
                            opacity: 1,
                            x: 0,
                          }}
                          exit={{
                            opacity: 0,
                          }}
                        >
                          <FiSend size={17} />
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
  
        <style>{`
          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  };
  
  export default ChatBot;